import { motion } from 'framer-motion';
import { useState } from 'react';
import { RefreshCcw, AlertTriangle, ArrowRightLeft, GitBranch, Lightbulb } from 'lucide-react';

interface UseCase {
  id: string;
  icon: React.ElementType;
  title: string;
  scenario: string;
  howItWorks: string[];
  agents: number;
  result: string;
}

const useCases: UseCase[] = [
  {
    id: 'refactor',
    icon: RefreshCcw,
    title: 'Multi-Service Refactor',
    scenario: 'Rename a domain concept across 50 microservices',
    howItWorks: [
      'Spawn agents per service in parallel',
      'Each agent: analyze, plan changes, await approval',
      'Approved changes execute, results converge',
      'Artifact: unified PR across all repos'
    ],
    agents: 50,
    result: '50 services updated in one session'
  },
  {
    id: 'incident',
    icon: AlertTriangle,
    title: 'Incident Response',
    scenario: 'Production alert: latency spike in checkout',
    howItWorks: [
      'Agent pulls recent deploys, logs, metrics',
      'Context hierarchy: incident → service → shared knowledge',
      'Surfaces candidates for human triage',
      'Approved fix deployed, learnings captured'
    ],
    agents: 3,
    result: 'MTTR reduced from hours to minutes'
  },
  {
    id: 'migration',
    icon: ArrowRightLeft,
    title: 'Library Migration',
    scenario: 'Migrate from Moment.js to date-fns across monorepo',
    howItWorks: [
      'Pattern detection: find all Moment usage',
      'Transform agent generates migration map',
      'Parallel execution with conflict detection',
      'Test suite validates each transformation'
    ],
    agents: 12,
    result: '2,300 call sites migrated safely'
  },
  {
    id: 'parallel',
    icon: GitBranch,
    title: 'Parallel Feature Dev',
    scenario: 'Build auth, payments, notifications simultaneously',
    howItWorks: [
      'Three streams, three agent teams',
      'Shared store: API contracts, schemas',
      'Integration points surface to Attention Set',
      'Artifacts promote to shared when ready'
    ],
    agents: 9,
    result: 'Feature velocity 3x with coordination'
  },
  {
    id: 'poc',
    icon: Lightbulb,
    title: 'Strategic Exploration',
    scenario: 'Evaluate 3 approaches to real-time sync',
    howItWorks: [
      'Agent per approach: WebSocket, SSE, polling',
      'Each builds proof-of-concept implementation',
      'Benchmark results surface for comparison',
      'Winner promoted, others archived'
    ],
    agents: 3,
    result: 'Technical decision with working code'
  }
];

export function UseCasesV2() {
  const [activeCase, setActiveCase] = useState<string>('refactor');

  const active = useCases.find(uc => uc.id === activeCase) || useCases[0];

  return (
    <section className="py-24 bg-surface-900">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
            Real Workflows, Real Results
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            See how orchestration transforms complex engineering tasks.
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {useCases.map((uc) => (
            <button
              key={uc.id}
              onClick={() => setActiveCase(uc.id)}
              className={`
                inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all
                ${activeCase === uc.id
                  ? 'bg-primary-500 text-white'
                  : 'bg-surface-800 text-text-secondary hover:bg-surface-700 hover:text-text-primary border border-surface-600'
                }
              `}
            >
              <uc.icon className="w-4 h-4" />
              {uc.title}
            </button>
          ))}
        </div>

        {/* Active Case Detail */}
        <motion.div
          key={active.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="grid lg:grid-cols-2 gap-8"
        >
          {/* Left: Description */}
          <div className="bg-surface-800 rounded-xl border border-surface-600 p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-lg bg-primary-500/10">
                <active.icon className="w-6 h-6 text-primary-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-text-primary">{active.title}</h3>
                <p className="text-text-secondary text-sm">{active.scenario}</p>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-text-tertiary uppercase tracking-wider">How it works</h4>
              {active.howItWorks.map((step, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary-500/20 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-xs text-primary-400 font-semibold">{i + 1}</span>
                  </div>
                  <p className="text-text-secondary">{step}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Results */}
          <div className="bg-surface-800 rounded-xl border border-surface-600 p-8 flex flex-col">
            <h4 className="text-sm font-semibold text-text-tertiary uppercase tracking-wider mb-6">Results</h4>

            <div className="flex-1 flex flex-col justify-center">
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="text-center p-4 bg-surface-900 rounded-lg">
                  <div className="text-3xl font-bold text-primary-400 mb-1">{active.agents}</div>
                  <div className="text-sm text-text-secondary">Parallel Agents</div>
                </div>
                <div className="text-center p-4 bg-surface-900 rounded-lg">
                  <div className="text-3xl font-bold text-success-400 mb-1">1</div>
                  <div className="text-sm text-text-secondary">Human Session</div>
                </div>
              </div>

              <div className="p-4 bg-gradient-to-r from-success-500/10 to-primary-500/10 rounded-lg border border-success-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-success-400" />
                  <span className="text-sm font-semibold text-success-400">Outcome</span>
                </div>
                <p className="text-text-primary font-medium">{active.result}</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
